/* LedPro model finder — buscador de modelos INTELIGENTE (fuzzy real, tolera errores de tipeo).
   Carga un índice de modelos desde /products.json (una sola vez, cacheado) y hace matching
   difuso 100% del lado del cliente. Al elegir un modelo lleva a los RESULTADOS de ese modelo
   (/search?q=<modelo>), no a un producto puntual.

   Se auto-inicializa sobre cada contenedor [data-finder].
   Atributos del contenedor:
     data-finder-scope  : "motocross" | "calle" | "all"
     data-finder-search-url : {{ routes.search_url }}
     data-finder-limit  : cantidad máxima de resultados (default 8)
   Dentro del contenedor:
     [data-finder-form]    : <form>
     [data-finder-input]   : <input type="search">
     [data-finder-results] : contenedor de resultados
     [data-finder-term]    : (opcional) chips de búsqueda rápida
     [data-finder-empty]   : (opcional) elemento "sin resultados"
*/
(function () {
  'use strict';

  function normalize(s) {
    return (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function toTokens(s) {
    return normalize(s).split(' ').filter(Boolean);
  }

  // Distancia de Levenshtein (para tolerar typos)
  function levenshtein(a, b) {
    var la = a.length, lb = b.length;
    if (la === 0) return lb;
    if (lb === 0) return la;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      }
      for (j = 0; j <= lb; j++) prev[j] = cur[j];
    }
    return prev[lb];
  }

  function maxEditsFor(len) {
    if (len <= 3) return 1;
    if (len <= 6) return 2;
    return 3;
  }

  // Quita el prefijo "Kit de Gráficas para Motocross/Moto " y el sufijo " - Diseño N"
  function cleanModelName(title) {
    var t = title || '';
    t = t.replace(/^\s*kit\s+de\s+gr[aá]ficas\s+para\s+(motocross|moto)\s+/i, '');
    t = t.replace(/^\s*kit\s+de\s+gr[aá]ficas\s+(para\s+)?/i, '');
    t = t.replace(/\s*[-–]\s*dise[nñ]o\s*\d+\s*$/i, '');
    t = t.replace(/\s*[-–]\s*\d+\s*$/, '');
    return t.trim() || title;
  }

  // ---- Índice de catálogo (compartido entre todos los finders de la página) ----
  var catalogPromise = null;

  function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async function () {
      var models = [];
      var seen = {};
      for (var page = 1; page <= 8; page++) {
        var res;
        try {
          res = await fetch('/products.json?limit=250&page=' + page, { headers: { 'Accept': 'application/json' } });
        } catch (e) { break; }
        if (!res.ok) break;
        var data = await res.json();
        var prods = (data && data.products) || [];
        if (!prods.length) break;
        prods.forEach(function (p) {
          var type = (p.product_type || '');
          var lt = type.toLowerCase();
          if (lt !== 'moto' && lt !== 'motocross' && lt !== 'karting') return;
          var title = p.title || '';
          var low = title.toLowerCase();
          if (low.indexOf('pieza individual') !== -1) return;
          if (low.indexOf('personalizado') !== -1 || low.indexOf('generic') !== -1 ||
            low.indexOf('seña') !== -1 || low.indexOf('sena') !== -1) return;
          var name = cleanModelName(title);
          var key = normalize(name) + '|' + lt;
          if (seen[key]) return;
          seen[key] = true;
          models.push({
            name: name,
            type: type,
            normName: normalize(name),
            tokens: toTokens(name),
            url: '/search?q=' + encodeURIComponent(name) + '&type=product'
          });
        });
        if (prods.length < 250) break;
      }
      return models;
    })();
    return catalogPromise;
  }

  function scopeAllows(scope, type) {
    var pt = (type || '').toLowerCase();
    if (scope === 'motocross') return pt === 'motocross';
    if (scope === 'calle') return pt === 'moto';
    return pt === 'motocross' || pt === 'moto' || pt === 'karting';
  }

  function scoreModel(qTokens, qJoined, model) {
    var name = model.normName;
    var mTokens = model.tokens;
    var s = 0, matched = 0;

    if (name === qJoined) s += 200;
    if (name.indexOf(qJoined) === 0) s += 80;
    else if (name.indexOf(qJoined) !== -1) s += 45;

    qTokens.forEach(function (qt) {
      var best = 0;
      for (var k = 0; k < mTokens.length; k++) {
        var mt = mTokens[k];
        if (mt === qt) { best = Math.max(best, 24); continue; }
        if (mt.indexOf(qt) === 0) { best = Math.max(best, 16); continue; }
        if (qt.indexOf(mt) === 0 && mt.length >= 2) { best = Math.max(best, 12); continue; }
        if (mt.indexOf(qt) !== -1) { best = Math.max(best, 10); continue; }
        if (qt.length >= 2) {
          var d = levenshtein(qt, mt);
          if (d <= maxEditsFor(Math.max(qt.length, mt.length))) {
            best = Math.max(best, 9 - d);
          }
        }
      }
      if (best > 0) { s += best; matched++; }
    });

    if (matched === 0) return -1;
    s += matched * 6;
    return s;
  }

  function LedProFinder(root) {
    this.root = root;
    this.scope = root.getAttribute('data-finder-scope') || 'all';
    this.searchUrl = root.getAttribute('data-finder-search-url') || '/search';
    this.limit = parseInt(root.getAttribute('data-finder-limit'), 10) || 8;
    this.input = root.querySelector('[data-finder-input]');
    this.results = root.querySelector('[data-finder-results]');
    this.form = root.querySelector('[data-finder-form]');
    this.emptyEl = root.querySelector('[data-finder-empty]');
    this.current = [];
    this.activeIndex = -1;
    this.timer = null;
    if (this.input && this.results) this.bind();
  }

  LedProFinder.prototype.bind = function () {
    var self = this;
    this.input.addEventListener('input', function () { self.schedule(); });
    this.input.addEventListener('focus', function () {
      loadCatalog();
      if (self.current.length) self.open();
    });
    this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });
    if (this.form) {
      this.form.addEventListener('submit', function (e) { e.preventDefault(); self.goToBest(); });
    }
    this.root.querySelectorAll('[data-finder-term]').forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        var term = chip.getAttribute('data-term') || chip.textContent.trim();
        self.input.value = term;
        self.input.focus();
        self.run(term);
      });
    });
    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target)) self.close();
    });
  };

  LedProFinder.prototype.schedule = function () {
    var self = this;
    clearTimeout(this.timer);
    var q = this.input.value.trim();
    if (q.length < 2) { this.clear(); return; }
    this.timer = setTimeout(function () { self.run(q); }, 180);
  };

  LedProFinder.prototype.run = function (q) {
    var self = this;
    var query = (q || '').trim();
    if (query.length < 2) { this.clear(); return; }
    this._lastQuery = query;
    loadCatalog().then(function (models) {
      if (self.input.value.trim() !== query && self._lastQuery !== query) return;
      self.render(query, models || []);
    });
  };

  LedProFinder.prototype.render = function (query, models) {
    var self = this;
    var qTokens = toTokens(query);
    var qJoined = qTokens.join(' ');

    var scored = [];
    models.forEach(function (m) {
      if (!scopeAllows(self.scope, m.type)) return;
      var sc = scoreModel(qTokens, qJoined, m);
      if (sc < 0) return;
      scored.push({ url: m.url, name: m.name, type: m.type, score: sc });
    });
    scored.sort(function (a, b) { return b.score - a.score; });
    scored = scored.slice(0, this.limit);
    this.current = scored;
    this.activeIndex = -1;

    if (!scored.length) {
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }
    if (this.emptyEl) this.emptyEl.hidden = true;

    this.results.innerHTML = scored.map(function (item, i) {
      return '<a class="lp-finder-result" href="' + item.url + '" data-finder-item="' + i + '">' +
        '<span class="lp-finder-result-name">' + escapeHtml(item.name) + '</span>' +
        '</a>';
    }).join('');
    this.results.hidden = false;
    this.open();

    this.results.querySelectorAll('[data-finder-item]').forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        self.setActive(parseInt(el.getAttribute('data-finder-item'), 10));
      });
    });
  };

  LedProFinder.prototype.setActive = function (i) {
    this.activeIndex = i;
    this.results.querySelectorAll('[data-finder-item]').forEach(function (el, idx) {
      el.classList.toggle('is-active', idx === i);
    });
  };

  LedProFinder.prototype.onKeydown = function (e) {
    if (!this.current.length) {
      if (e.key === 'Enter') { e.preventDefault(); this.goToBest(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setActive(Math.min(this.activeIndex + 1, this.current.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setActive(Math.max(this.activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (this.activeIndex >= 0) window.location.href = this.current[this.activeIndex].url;
      else this.goToBest();
    } else if (e.key === 'Escape') {
      this.close();
    }
  };

  LedProFinder.prototype.goToBest = function () {
    if (this.current.length) {
      var idx = this.activeIndex >= 0 ? this.activeIndex : 0;
      window.location.href = this.current[idx].url;
    } else if (this.input.value.trim()) {
      window.location.href = this.searchUrl + '?q=' + encodeURIComponent(this.input.value.trim()) + '&type=product';
    }
  };

  LedProFinder.prototype.open = function () { if (this.results) this.results.hidden = false; };
  LedProFinder.prototype.close = function () { if (this.results) this.results.hidden = true; };
  LedProFinder.prototype.clear = function () {
    this.current = [];
    this.activeIndex = -1;
    if (this.results) { this.results.innerHTML = ''; this.results.hidden = true; }
    if (this.emptyEl) this.emptyEl.hidden = true;
  };

  function escapeHtml(s) {
    return (s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function init() {
    document.querySelectorAll('[data-finder]').forEach(function (root) {
      if (root.__lpFinder) return;
      root.__lpFinder = new LedProFinder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
